import React, { useMemo } from 'react';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import ActionsDropdown from 'components/partials/actions-dropdown/actions-dropdown';
import { Switch } from 'ui/switch';

import MetricCard from './metric-card';

import Icons from 'assets/icons';

import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import type { MetricCardData } from '../results-step.types';

interface MetricsSectionProps {
  cards: MetricCardData[];
  showNetComprehensiveFee: boolean;
  onNetComprehensiveFeeChange: (value: boolean) => void;
  actualYearLabel: string;
  isRecalculating?: boolean;
  onShowCoa: () => void;
  isCoaDisabled?: boolean;
  onCompareEstimates?: () => void;
  canCompare?: boolean;
  onDownloadDataset: () => void;
  isDownloadingDataset?: boolean;
  canDownloadDataset?: boolean;
}

const MetricsSection = ({
  cards,
  showNetComprehensiveFee,
  onNetComprehensiveFeeChange,
  actualYearLabel,
  isRecalculating = false,
  onShowCoa,
  isCoaDisabled,
  onCompareEstimates,
  canCompare = false,
  onDownloadDataset,
  isDownloadingDataset = false,
  canDownloadDataset = false,
}: MetricsSectionProps): React.JSX.Element => {
  const dropdownOptions: ActionsDropdownOption[] = useMemo(
    () => [
      {
        label: 'Compare estimates',
        onClick: onCompareEstimates,
        isDisabled: !canCompare,
        customClass: 'font-normal text-gray-700',
      },
    ],
    [onCompareEstimates, canCompare]
  );

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="results-metrics-section"
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <h3 className="text-lg font-bold text-gray-700">
            Current first year estimates
          </h3>

          <Box
            sx={{
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: 'tokens.gray.700',
            }}
          >
            <Switch
              size="small"
              label="Net comprehensive fee data"
              checked={showNetComprehensiveFee}
              onChange={(_event, checked) =>
                onNetComprehensiveFeeChange(checked)
              }
              data-testid="results-net-comp-fee-toggle"
            />
          </Box>
        </Stack>

        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <Button
            type="button"
            variant="reserve-outlined"
            size="sm"
            onClick={onShowCoa}
            disabled={isCoaDisabled}
            className="shadow-sm"
            data-testid="results-show-coa-button"
          >
            Show CoA
          </Button>

          {canDownloadDataset && (
            <Button
              type="button"
              variant="brand-outlined"
              size="sm"
              onClick={onDownloadDataset}
              loading={isDownloadingDataset}
              iconAfter={<Icons.Download aria-hidden="true" />}
              className="shadow-sm"
              data-testid="results-download-dataset-button"
            >
              Download dataset
            </Button>
          )}

          <ActionsDropdown
            options={dropdownOptions}
            variant="brand-text"
            customClass="size-8 p-1.5 border-none aria-expanded:bg-lime-100"
            menuCustomClass="border-none shadow-md p-1.5 gap-0"
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '@media (min-width: 780px)': { flexDirection: 'row' },
        }}
      >
        {cards.map((card) => (
          <MetricCard
            key={card.key}
            card={card}
            actualYearLabel={actualYearLabel}
            isLoading={isRecalculating}
          />
        ))}
      </Box>
    </section>
  );
};

export default MetricsSection;
